import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { CAREER_COLOR } from '@/components/domain/seminar/seminarDisplay';

/*
 * 쿠폰 목록 카드 공용 부품 (docs/seminar-coupon-spec.md "문구 규칙").
 *   CouponRow       — [금액 72px | 이름·설명 | 버튼 64×36]  세미나 상세·내 쿠폰함·강사 폼
 *   CouponSelectRow — [라디오 40px | 이름·설명 | 할인액]     결제 모달
 *   CouponList      — 카드 사이 8px
 *   CouponNotes     — 화면 맨 아래 안내(문장마다 줄바꿈)
 * 숫자는 고정폭(tabular-nums)·오른쪽 정렬.
 */

const PRIMARY: string = CAREER_COLOR;
const BORDER: string = '#e5e7eb';

export const CouponList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const RowBox = styled.div<{ muted?: boolean; clickable?: boolean; bare?: boolean; withFooter?: boolean }>`
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 12px;
  /* 아래 버튼 줄이 있는 카드(강사 폼 — 시안 ui-cA)는 UI 가이드 카드 반경 16·여백 14/16 */
  padding: ${({ bare, withFooter }) => (bare ? '0' : withFooter ? '14px 16px' : '12px')};
  background: ${({ bare }) => (bare ? 'transparent' : '#fff')};
  border: ${({ bare }) => (bare ? 'none' : `1px solid ${BORDER}`)};
  border-radius: ${({ withFooter }) => (withFooter ? 16 : 12)}px;
  box-sizing: border-box;
  min-width: 0;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};

  /* 흐리게는 금액·이름만 — 버튼(재개 등)·아래 버튼 줄은 그대로 눌러 보이게 */
  > :not(button):not(.coupon-row-footer) {
    opacity: ${({ muted }) => (muted ? 0.55 : 1)};
  }
`;

const AmountCell = styled.div<{ muted?: boolean }>`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding-right: 10px;
  border-right: 1px dashed ${BORDER};
  text-align: center;
  font-variant-numeric: tabular-nums;
  color: ${({ muted }) => (muted ? '#9ca3af' : PRIMARY)};
  min-width: 0;
`;

const AmountMain = styled.b`
  font-size: 17px;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
`;

const AmountSub = styled.small`
  font-size: 11px;
  line-height: 1.3;
  color: #6b7280;
  white-space: nowrap;
`;

const Info = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Name = styled.div`
  font-size: 15px;
  font-weight: 800;
  line-height: 1.35;
  color: #111827;
  word-break: keep-all;
  overflow-wrap: anywhere;
`;

const Desc = styled.div`
  font-size: 13px;
  line-height: 1.45;
  color: #6b7280;
  word-break: keep-all;
  overflow-wrap: anywhere;
`;

const TagLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 2px;
`;

export type CouponTagTone = 'muted' | 'danger' | 'code';

const Tag = styled.span<{ tone: CouponTagTone }>`
  display: inline-block;
  padding: 1px 7px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.6;
  letter-spacing: ${({ tone }) => (tone === 'code' ? '0.02em' : 'normal')};
  background: ${({ tone }) => (tone === 'danger' ? '#fef2f2' : tone === 'code' ? '#e8f2f1' : '#f3f4f6')};
  color: ${({ tone }) => (tone === 'danger' ? '#b91c1c' : tone === 'code' ? PRIMARY : '#4b5563')};
  font-variant-numeric: tabular-nums;
`;

/** primary 받기·재개 / soft 중지(회색이지만 누를 수 있음) / ghost 테두리 / done 받음 / disabled 마감 */
export type CouponActionVariant = 'primary' | 'soft' | 'done' | 'disabled' | 'ghost';

const ActionButton = styled.button<{ variant: CouponActionVariant }>`
  width: 64px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 10px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${({ variant }) => (variant === 'ghost' ? `1px solid ${BORDER}` : 'none')};
  background: ${({ variant }) => (variant === 'primary' ? PRIMARY : variant === 'ghost' ? '#fff' : '#f3f4f6')};
  color: ${({ variant }) => (variant === 'primary' ? '#fff' : variant === 'ghost' || variant === 'soft' ? '#4b5563' : '#9ca3af')};
  cursor: ${({ variant }) => (variant === 'primary' || variant === 'ghost' || variant === 'soft' ? 'pointer' : 'default')};

  &:disabled {
    cursor: default;
  }
`;

export type CouponStatusTone = 'pending' | 'active' | 'paused';

/** 코드 옆 한 줄 상태 — 저장하면 발급돼요(주황) · 진행 중(청록) · 중지됨(진한 글자) */
const StatusText = styled.span<{ tone: CouponStatusTone }>`
  font-size: 13px;
  font-weight: 700;
  line-height: 1.6;
  color: ${({ tone }) => (tone === 'pending' ? '#C2410C' : tone === 'active' ? '#2F6B66' : '#1F2A28')};
`;

/** 카드 아래 버튼 줄(강사 폼 — 시안 ui-cA): 구분선 + 같은 모양 버튼(높이 40·반경 10·흰 바탕 #D3DBD8 테두리·700), 개수만큼 균등 */
const FooterRow = styled.div<{ count: number }>`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(${({ count }) => count}, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #E3E8E6;
`;

const FooterButton = styled.button<{ danger: boolean }>`
  height: 40px;
  padding: 0 6px;
  border-radius: 10px;
  border: 1px solid ${({ danger }) => (danger ? '#F9C7D2' : '#D3DBD8')};
  background: #fff;
  color: ${({ danger }) => (danger ? '#E11D48' : '#1F2A28')};
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    color: #C7CFCC;
    cursor: default;
  }
`;

export interface CouponFooterButton {
  label: ReactNode;
  onClick?: () => void;
  /** 삭제처럼 빨강 */
  danger?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
}

export interface CouponTag {
  text: string;
  tone?: CouponTagTone;
}

export interface CouponAction {
  label: ReactNode;
  variant: CouponActionVariant;
  onClick?: () => void;
  busy?: boolean;
  ariaLabel?: string;
}

interface CouponRowProps {
  amount: string;
  amountSub?: string;
  title: string;
  description?: ReactNode;
  tags?: CouponTag[];
  action?: CouponAction | null;
  /** 사용·만료·중지처럼 흐리게 */
  muted?: boolean;
  /** 카드 본문을 누르면(버튼 제외) — 강사 쿠폰 수정 등 */
  onClick?: () => void;
  /** 테두리·안쪽 여백 없이(바깥 카드에 넣을 때 — 내 쿠폰함) */
  bare?: boolean;
  /** 태그를 이름 바로 아래(설명 위)에 — 강사 폼의 코드 */
  tagsFirst?: boolean;
  /** 코드 옆 상태 글자(강사 폼) */
  status?: { text: string; tone: CouponStatusTone } | null;
  /** 카드 아래 버튼 줄(강사 폼) — 있으면 오른쪽 버튼 대신 쓴다 */
  footer?: CouponFooterButton[];
}

const TagList: React.FC<{ tags: CouponTag[]; status?: CouponRowProps['status'] }> = ({ tags, status }) => (
  <TagLine>
    {tags.map((tag: CouponTag) => (
      <Tag key={tag.text} tone={tag.tone ?? 'muted'}>{tag.text}</Tag>
    ))}
    {status && <StatusText tone={status.tone}>{status.text}</StatusText>}
  </TagLine>
);

/** [금액 72px | 이름·설명 | 버튼 64×36] */
export const CouponRow: React.FC<CouponRowProps> = ({
  amount,
  amountSub,
  title,
  description,
  tags = [],
  action,
  muted = false,
  onClick,
  bare = false,
  tagsFirst = false,
  status = null,
  footer,
}) => (
  <RowBox
    bare={bare}
    withFooter={!!footer && footer.length > 0}
    muted={muted}
    clickable={Boolean(onClick)}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e: React.KeyboardEvent<HTMLDivElement>) => { if (e.key === 'Enter') onClick(); } : undefined}
  >
    <AmountCell muted={muted}>
      <AmountMain>{amount}</AmountMain>
      {amountSub && <AmountSub>{amountSub}</AmountSub>}
    </AmountCell>
    <Info>
      <Name>{title}</Name>
      {tagsFirst && (tags.length > 0 || status) && <TagList tags={tags} status={status} />}
      {description && <Desc>{description}</Desc>}
      {!tagsFirst && tags.length > 0 && <TagList tags={tags} />}
    </Info>
    {footer ? (
      <span />
    ) : action ? (
      <ActionButton
        type="button"
        variant={action.variant}
        disabled={action.variant === 'done' || action.variant === 'disabled' || action.busy}
        aria-label={action.ariaLabel}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          action.onClick?.();
        }}
      >
        {action.label}
      </ActionButton>
    ) : (
      <span />
    )}
    {footer && footer.length > 0 && (
      <FooterRow className="coupon-row-footer" count={footer.length}>
        {footer.map((button: CouponFooterButton, index: number) => (
          <FooterButton
            key={index}
            type="button"
            danger={!!button.danger}
            disabled={button.disabled}
            aria-label={button.ariaLabel}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              button.onClick?.();
            }}
          >
            {button.label}
          </FooterButton>
        ))}
      </FooterRow>
    )}
  </RowBox>
);

/* ───────── 결제 모달 선택형 ───────── */

const SelectBox = styled.label<{ selected: boolean; disabled: boolean }>`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 4px;
  padding: 12px 14px 12px 6px;
  border-radius: 12px;
  border: ${({ selected }) => (selected ? `2px solid ${PRIMARY}` : `1px solid ${BORDER}`)};
  margin: ${({ selected }) => (selected ? '0' : '1px')};
  background: ${({ selected }) => (selected ? '#f1f7f6' : '#fff')};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  box-sizing: border-box;
  min-width: 0;
  position: relative;

  /* 키보드로 고를 때 보이는 포커스 링 */
  &:has(input:focus-visible) {
    outline: 2px solid ${PRIMARY};
    outline-offset: 2px;
  }

  input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    pointer-events: none;
  }
`;

const RadioDot = styled.span<{ selected: boolean; disabled: boolean }>`
  justify-self: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  box-sizing: border-box;
  border: 2px solid ${({ selected, disabled }) => (selected ? PRIMARY : disabled ? '#e5e7eb' : '#d1d5db')};
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ selected }) => (selected ? PRIMARY : 'transparent')};
  }
`;

const SelectName = styled.div<{ disabled: boolean }>`
  font-size: 15px;
  font-weight: 800;
  line-height: 1.35;
  color: ${({ disabled }) => (disabled ? '#9ca3af' : '#111827')};
  word-break: keep-all;
  overflow-wrap: anywhere;
`;

const SelectDesc = styled.div<{ disabled: boolean }>`
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.45;
  color: ${({ disabled }) => (disabled ? '#b6bcc6' : '#6b7280')};
  /* 못 쓰는 이유는 끝까지 보여 준다 */
  white-space: ${({ disabled }) => (disabled ? 'normal' : 'nowrap')};
  word-break: keep-all;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SelectAmount = styled.b<{ tone: 'discount' | 'muted' }>`
  padding-left: 8px;
  font-size: 15px;
  font-weight: 800;
  white-space: nowrap;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: ${({ tone }) => (tone === 'discount' ? '#c2410c' : '#9ca3af')};
`;

interface CouponSelectRowProps {
  name: string;
  title: string;
  description?: string;
  amountText: string;
  amountTone?: 'discount' | 'muted';
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

/** [라디오 40px | 이름·설명 | 할인액] */
export const CouponSelectRow: React.FC<CouponSelectRowProps> = ({
  name,
  title,
  description,
  amountText,
  amountTone = 'discount',
  selected,
  disabled = false,
  onSelect,
}) => (
  <SelectBox selected={selected} disabled={disabled}>
    <input
      type="radio"
      name={name}
      checked={selected}
      disabled={disabled}
      onChange={() => { if (!disabled) onSelect(); }}
    />
    <RadioDot selected={selected} disabled={disabled} aria-hidden="true" />
    <div style={{ minWidth: 0 }}>
      <SelectName disabled={disabled}>{title}</SelectName>
      {description && <SelectDesc disabled={disabled}>{description}</SelectDesc>}
    </div>
    <SelectAmount tone={disabled ? 'muted' : amountTone}>{amountText}</SelectAmount>
  </SelectBox>
);

/* ───────── 안내 문구 — 화면 맨 아래, 문장마다 줄바꿈 ───────── */

const NotesBox = styled.div`
  margin-top: 12px;
  font-size: 12px;
  line-height: 1.7;
  color: #9ca3af;
  word-break: keep-all;
`;

export const CouponNotes: React.FC<{ lines: string[]; className?: string }> = ({ lines, className }) =>
  lines.length > 0 ? (
    <NotesBox className={className}>
      {lines.map((line: string) => (
        <div key={line}>· {line}</div>
      ))}
    </NotesBox>
  ) : null;

export default CouponRow;
